import Ionicons from "@expo/vector-icons/Ionicons";
import {
  useLocalSearchParams,
  useRouter,
  type ErrorBoundaryProps,
} from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useMemo } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import { PackCheckout } from "@/components/billing/pack-checkout";
import { ErrorFallback } from "@/components/ui/error-fallback";
import { Spacing, type ThemeColors } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import i18n from "@/i18n";

const FONT_FAMILY = Platform.select({
  ios: "Avenir Next",
  android: "sans-serif",
  default: undefined,
});

/**
 * In-app practice pack checkout, entered as /pack-checkout?pack=<id>.
 *
 * Screen chrome only — the payment flow lives in PackCheckout, which resolves
 * to Embedded Checkout on web and to a "manage on web" stub on native, the
 * same split `subscribe.tsx` uses.
 */
/**
 * Keeps a render failure here from taking the whole app down. Billing is the
 * one place where a blank screen is ambiguous about money: the buyer cannot
 * tell whether they were charged.
 */
export function ErrorBoundary({ error, retry }: ErrorBoundaryProps) {
  return <ErrorFallback error={error} retry={retry} scope="billing" />;
}

export default function PackCheckoutScreen() {
  useTranslation();
  const router = useRouter();
  const c = useTheme();
  const styles = useMemo(() => createStyles(c), [c]);

  const params = useLocalSearchParams<{ pack?: string }>();
  // Passed straight through as an id. The server validates it against
  // PRACTICE_PACKS and prices it there, so a hand-edited URL buys nothing.
  const packId = typeof params.pack === "string" ? params.pack : "";

  function close() {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.replace("/practice");
  }

  return (
    <SafeAreaView style={styles.safe} edges={["top"]}>
      <StatusBar style="auto" />

      <View style={styles.topBar}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={i18n.t("billing.common.goBackA11y")}
          onPress={close}
          hitSlop={10}
          style={({ pressed }) => [styles.iconButton, pressed && styles.pressed]}
        >
          <Ionicons name="chevron-back" size={24} color={c.text} />
        </Pressable>
        <Text style={styles.topBarTitle}>
          {i18n.t("practice.addRepliesTitle")}
        </Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <PackCheckout packId={packId} />
      </ScrollView>
    </SafeAreaView>
  );
}

function createStyles(c: ThemeColors) {
  return StyleSheet.create({
    safe: { flex: 1, backgroundColor: c.background },
    topBar: {
      minHeight: 56,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: Spacing.md,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.border,
    },
    iconButton: {
      width: 40,
      height: 40,
      alignItems: "center",
      justifyContent: "center",
    },
    topBarTitle: {
      fontFamily: FONT_FAMILY,
      fontSize: 17,
      fontWeight: "700",
      color: c.text,
    },
    headerSpacer: { width: 40 },
    content: {
      width: "100%",
      maxWidth: 1040,
      alignSelf: "center",
      paddingHorizontal: Spacing.lg,
      paddingBottom: Spacing.xxxl,
      gap: Spacing.sm,
    },
    pressed: { opacity: 0.72 },
  });
}
